import { useEffect, useMemo, useRef, useState } from 'react';
import { hashKey, useQueryClient } from '@tanstack/react-query';

import {
    beginPenSwitchRender,
    cancelPenSwitchRender,
    cancelPenSwitchRenderUnlessCage,
} from '@/analytics/core/penSwitchRenderTiming';
import { readSelectedFishGroupId } from '@/contexts/cageFilter/params';
import { cageOverviewQueryKey } from '@/domain/cages/cageOverviewQuery';
import {
    isCageOverviewCacheWarm,
    prefetchCageDashboard,
} from '@/domain/cages/prefetchCageDashboard';
import { scheduleIdle } from '@/utils/scheduleIdle';

import {
    cageDashboardTargetFromPenHref,
    cageOverviewParamsFromSearch,
    penNeighborDashboardTargets,
} from './penNeighborPrefetch';
import { orderedBreadcrumbCages, penStepHref } from './penStep';
import type { BreadcrumbCage, CagesByStatus } from './types';

interface PenPrefetchInput {
    cagesByStatus: CagesByStatus;
    cageId: number | undefined;
    clientId: number | undefined;
    locationId: number | undefined;
    pagePath: string;
    search: string;
    tracksFishGroups: boolean;
}

function isDashboardPath(pagePath: string): boolean {
    return pagePath === '/dashboard';
}

function targetKey(
    targets: {
        cageId: number;
        fishGroupId?: number;
        from?: string;
        to?: string;
        daypartTag: string;
    }[]
): string {
    return targets
        .map(
            (target) =>
                `${target.cageId}:${target.fishGroupId ?? ''}:${target.from ?? ''}:${target.to ?? ''}:${target.daypartTag}`
        )
        .join('|');
}

/**
 * After the current pen's overview query has succeeded, prefetch the previous and
 * next pens while the browser is idle, and only on the cage dashboard. The current
 * pen is not asked for again.
 */
export function useIdlePenNeighborPrefetch(input: PenPrefetchInput): void {
    const { cagesByStatus, cageId, clientId, locationId, pagePath, search, tracksFishGroups } =
        input;
    const queryClient = useQueryClient();
    const cages = useMemo(() => orderedBreadcrumbCages(cagesByStatus), [cagesByStatus]);
    const current = cages.find((cage) => cage.id === cageId);
    const fishGroupId = tracksFishGroups ? readSelectedFishGroupId(search) : undefined;
    const overviewParams = cageOverviewParamsFromSearch(
        search,
        current?.smolt === true,
        fishGroupId
    );
    const overviewKeyRef = useRef(
        cageId != null ? cageOverviewQueryKey(cageId, overviewParams) : null
    );
    overviewKeyRef.current = cageId != null ? cageOverviewQueryKey(cageId, overviewParams) : null;
    const overviewHash = overviewKeyRef.current != null ? hashKey(overviewKeyRef.current) : '';

    const [loaded, setLoaded] = useState(false);

    useEffect(() => {
        const overviewKey = overviewKeyRef.current;
        if (overviewKey == null) {
            setLoaded(false);
            return;
        }
        const sync = () => {
            setLoaded(queryClient.getQueryState(overviewKey)?.status === 'success');
        };
        sync();
        return queryClient.getQueryCache().subscribe((event) => {
            if (event.query.queryHash !== overviewHash) {
                return;
            }
            sync();
        });
    }, [overviewHash, queryClient]);

    const targets = useMemo(() => {
        if (clientId == null || locationId == null) {
            return [];
        }
        return penNeighborDashboardTargets({
            cages,
            currentCageId: cageId,
            clientId,
            locationId,
            pagePath,
            search,
            tracksFishGroups,
        });
    }, [cages, cageId, clientId, locationId, pagePath, search, tracksFishGroups]);

    const targetsRef = useRef(targets);
    targetsRef.current = targets;
    const serializedTargets = targetKey(targets);

    useEffect(() => {
        if (!isDashboardPath(pagePath) || !loaded || serializedTargets === '') {
            return;
        }
        let cancelled = false;
        const cancelIdle = scheduleIdle(() => {
            if (cancelled) {
                return;
            }
            for (const target of targetsRef.current) {
                void prefetchCageDashboard(queryClient, target);
            }
        });
        return () => {
            cancelled = true;
            cancelIdle();
        };
    }, [loaded, pagePath, queryClient, serializedTargets]);
}

/**
 * Start the render timer for a dashboard step. Other pages keep `pen_switched`
 * and do not record a duration, because the dashboard overview never paints.
 */
export function useRecordPenSwitchRender(
    input: Omit<PenPrefetchInput, 'cagesByStatus' | 'cageId'> & { cageId?: number }
): (direction: 'previous' | 'next', cage: BreadcrumbCage) => void {
    const { cageId, clientId, locationId, pagePath, search, tracksFishGroups } = input;
    const queryClient = useQueryClient();

    useEffect(() => {
        if (!isDashboardPath(pagePath)) {
            cancelPenSwitchRender();
        }
        // The stepper unmounts with the cage route. The pending step is module-level,
        // so leaving the route has to clear it too: a later visit would otherwise
        // report a duration that includes the time away.
        //
        // This cleanup stays tied to the page, not the cage. A cage change would
        // run it on the way into the destination and drop the measurement the
        // click just started.
        return cancelPenSwitchRender;
    }, [pagePath]);

    useEffect(() => {
        if (cageId == null) {
            return;
        }
        // A dropdown detour leaves the pen this step is waiting on. The duration
        // would otherwise include the time spent elsewhere, then fire when the
        // reader comes back.
        cancelPenSwitchRenderUnlessCage(cageId);
    }, [cageId]);

    return (direction, cage) => {
        if (!isDashboardPath(pagePath) || clientId == null || locationId == null) {
            return;
        }
        const href = penStepHref({
            clientId,
            locationId,
            cage,
            pagePath,
            search,
            tracksFishGroups,
        });
        const target = cageDashboardTargetFromPenHref(href, cage.smolt === true);
        if (target == null) {
            return;
        }
        beginPenSwitchRender({
            source: direction,
            cageId: target.cageId,
            overviewCacheWarm: isCageOverviewCacheWarm(queryClient, target),
        });
    };
}
